import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import App from "./App.jsx";
import { ToastProvider } from "./components/Toast.jsx";
import { registerSW } from "./registerSW.js";
import { hydrateTheme } from "./shared/tokens.ts";
import { queryClient } from "./shared/queryClient.ts";
import "./shared/designTokens.css";
import "./shared/print.css";

// Hidrata light/dark do localStorage antes do primeiro render
hydrateTheme();

// ─── FIX #310: patch React.createElement (fallback de segurança) ─────────────
// A defesa PRIMÁRIA é o Vite plugin safeJsxRuntimePlugin em vite.config.js,
// que intercepta react/jsx-runtime em build-time e garante que TODOS os
// componentes usam a versão segura do jsx/jsxs.
//
// Este patch de React.createElement é uma camada SECUNDÁRIA que cobre:
// - Bibliotecas que usam React.createElement() diretamente (classic runtime)
// - Qualquer código que não passe pelo jsx-runtime automático
//
// Nota: NÃO patchamos mais react/jsx-runtime aqui — isso era ineficaz porque
// ES module imports são hoisted e avaliados ANTES deste código executar.

function safifyChild(child) {
  if (child == null || typeof child !== "object") return child;
  if (React.isValidElement(child)) return child;
  if (Array.isArray(child)) return child.map(safifyChild);
  const str = String(child);
  if (import.meta.env.PROD) {
    console.error("[FIX-310] Objeto interceptado como React child:", child);
    if (window.Sentry?.captureMessage) {
      window.Sentry.captureMessage("[FIX-310] Object as React child intercepted", {
        level: "warning",
        extra: { value: JSON.stringify(child).slice(0, 500), type: typeof child },
      });
    }
  } else {
    console.warn("[FIX-310-DEV] Objeto como React child:", child);
  }
  return str === "[object Object]" ? "" : str;
}

function safifyProps(props) {
  if (!props || props.children == null) return props;
  const c = props.children;
  if (typeof c !== "object" || React.isValidElement(c)) return props;
  if (Array.isArray(c)) {
    return { ...props, children: c.map(safifyChild) };
  }
  return { ...props, children: safifyChild(c) };
}

// Patch React.createElement — funciona porque componentes acessam via
// propriedade do objeto React em call-time (não captura binding em import-time)
const _origCE = React.createElement;
React.createElement = function patchedCreateElement(type, props, ...children) {
  if (children.length > 0) {
    return _origCE.call(this, type, safifyProps(props), ...children.map(safifyChild));
  }
  return _origCE.call(this, type, safifyProps(props));
};

// ─── Sentry: monitoramento de erros em produção ───────────────────────────────
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const IS_PROD = import.meta.env.PROD;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: IS_PROD ? "production" : "development",
    enabled: IS_PROD,
    release: import.meta.env.VITE_APP_VERSION || "lpa-portal@dev",
    tracesSampleRate: IS_PROD ? 0.1 : 0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection",
      /^Network Error/,
      /^Load failed/,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: IS_PROD ? 1.0 : 0,
  });
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            fontFamily: "sans-serif",
            background: "#0F172A",
            color: "#F1F5F9",
            gap: 16,
            padding: 24,
          }}
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#EF4444"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Algo deu errado</h2>
          <p
            style={{
              margin: 0,
              color: "#94A3B8",
              fontSize: 14,
              textAlign: "center",
              maxWidth: 400,
            }}
          >
            {IS_PROD
              ? "O erro foi registrado e nossa equipe será notificada. Tente recarregar a página."
              : `[DEV] ${error?.message || String(error)}`}
          </p>
          <details
            style={{
              color: "#64748B",
              fontSize: 11,
              fontFamily: "monospace",
              textAlign: "left",
              maxWidth: 500,
              cursor: "pointer",
            }}
          >
            <summary style={{ marginBottom: 4 }}>Detalhes do erro</summary>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                maxHeight: 160,
                overflow: "auto",
                margin: 0,
                padding: 8,
                background: "#1E293B",
                borderRadius: 6,
                fontSize: 10,
              }}
            >
              {error?.message}
              {"\n"}
              {error?.stack?.split("\n").slice(0, 8).join("\n")}
            </pre>
          </details>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={resetError}
              style={{
                padding: "10px 20px",
                borderRadius: 8,
                border: "none",
                background: "#2563EB",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Tentar novamente
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "10px 20px",
                borderRadius: 8,
                border: "1px solid #334155",
                background: "transparent",
                color: "#94A3B8",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Recarregar página
            </button>
          </div>
        </div>
      )}
      showDialog={false}
    >
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <App />
        </ToastProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// ─── PWA: Registra Service Worker (apenas em produção) ────────────────────────
registerSW();
